import cryptoJS from 'crypto-js';
import { getNumOfDaysBetweenDates } from 'utilities/dateHelper/dateHelper';
import UtagHelper from 'utilities/utagHelper';
import { ORIGIN_BY_CHANNEL } from 'constants/uTagConstants/origin';
import {
    PATH_DOCUMENTATION,
    PATH_DOCUMENTATION_FINISHED,
    PATH_DOCUMENTATION_RETRY,
} from 'constants/uTagConstants/paths';
import DOCUMENTATION_PRODUCT_NAME, {
    errorTag,
    recordEvent,
    recordEventCustomOnly,
    recordPage,
    RELIABLE,
} from 'utilities/uTagging';
import { ERROR_EN_DOCUMENTACION } from 'constants/analyticsErrors';
import { EVENT_ONBOARDING } from 'constants/uTagConstants/RAConstants';
import { DOCUMENT_TYPES } from 'constants/documents';

const buildTagging = ({
    products,
    immStepsState,
    userProfile,
    documentation,
    isTagDocumentationFired,
    isTagStandbyFired,
    shouldShowTgssConsent,
    shouldShowQRPage,
}) => {
    const {
        docNumber,
        recordId,
        userId,
        product,
        promoCode,
        identityMethod,
        investmentType,
        datetime,
        channel,
        clientRisk,
        documentationStatusCounter,
    } = userProfile;
    const productsDescription = products.get('productsDescription');
    const isWelcomeCard = immStepsState.get('welcomeCard');
    const JointHolderInfo = UtagHelper.getJointHolderInfoFromUserProfile(userProfile);
    const docNumberEncrypted = docNumber ? cryptoJS.SHA256(docNumber).toString() : '';
    const onboardingContFinal = documentationStatusCounter > 0 ? 2 : 1;
    const filesToUpload = documentation.get('filesToUpload');
    const isDAAL =
        filesToUpload.size > 0 && filesToUpload.every(({ docId }) => docId === DOCUMENT_TYPES.DAAL) ? 'DAAL' : '';

    const doTagDocumentationPage = isDocumentRetryBeenShown => {
        const getPathAndProcessDetail = () => {
            if (shouldShowQRPage) {
                return isDocumentRetryBeenShown
                    ? ['/onboarding/step8/documentationQR_review', 'documentationQR_review']
                    : ['/onboarding/step8/documentationQR', 'documentationQR'];
            }
            if (shouldShowTgssConsent) return ['/onboarding/step8/documentationVedacom', 'documentationVedacom'];

            return isDocumentRetryBeenShown
                ? [`/onboarding/step8/documentation${isDAAL}_review`, `documentation${isDAAL}_review`]
                : [`/onboarding/step8/documentation${isDAAL}`, `documentation${isDAAL}`];
        };

        if (isTagDocumentationFired.current) return;

        const timeComplete = getNumOfDaysBetweenDates(datetime);

        isTagDocumentationFired.current = true;

        const [path, processDetail] = getPathAndProcessDetail();

        return (
            !isWelcomeCard &&
            recordPage(
                path,
                recordId,
                userId,
                product,
                productsDescription,
                promoCode ? promoCode : '',
                JointHolderInfo,
                RELIABLE[identityMethod],
                timeComplete || '',
                investmentType,
                {
                    tipoProceso: 'onboarding',
                    pasoProceso: 'documentacion',
                    detalleProceso: 'finalizado',
                    onboardingContFinal,
                    nombreProducto: DOCUMENTATION_PRODUCT_NAME,
                    identificadorOnboarding: docNumberEncrypted,
                    event_name: 'step_documentation',
                    pageName: '/onboarding/step8/documentation',
                    sectionFamily: 'banking',
                    sectionCategory: 'onboarding',
                    sectionName: 'userOnboarding',
                    interactionType: 'funnel',
                    serviceType: 'onboarding',
                    processType: 'onboarding',
                    processStep: 'step8',
                    processDetail,
                    onboardingType: investmentType,
                    onboardingOrigin: ORIGIN_BY_CHANNEL[channel],
                    onboardingR: clientRisk || '',
                    onboardingIdMethod: RELIABLE[identityMethod],
                }
            )
        );
    };

    const doTagRecordStandByPage = () => {
        if (isTagStandbyFired.current) return;

        isTagStandbyFired.current = true;
        const fecha1 = new Date(userProfile.datetime);
        const fecha2 = new Date();
        const timeComplete = getNumOfDaysBetweenDates(fecha1, fecha2);

        !isWelcomeCard &&
            recordPage(
                PATH_DOCUMENTATION_FINISHED,
                recordId,
                userId,
                product,
                products.get('productsDescription'),
                promoCode ? promoCode : '',
                JointHolderInfo,
                RELIABLE[identityMethod],
                timeComplete,
                investmentType,
                {
                    identificadorOnboarding: docNumberEncrypted,
                    tipoProceso: 'onboarding',
                    pasoProceso: 'documentacion',
                    detalleProceso: 'finalizado-virtual',
                    categoriaEvento: EVENT_ONBOARDING,
                    accionEvento: 'documentacion',
                    etiquetaEvento: 'finalizado-virtual',
                    onboardingContFinal,
                    nombreProducto: DOCUMENTATION_PRODUCT_NAME,
                    event_name: 'step_success',
                    pageName: '/onboarding/standby',
                    sectionFamily: 'banking',
                    sectionCategory: 'onboarding',
                    sectionName: 'userOnboarding',
                    interactionType: 'funnel',
                    serviceType: 'onboarding',
                    processType: 'onboarding',
                    processStep: 'success',
                    onboardingType: investmentType,
                    onboardingR: clientRisk || '',
                    onboardingIdMethod: RELIABLE[identityMethod],
                    onboardingOrigin: ORIGIN_BY_CHANNEL[userProfile.channel],
                }
            );
    };

    const doTagDocumentRetry = () => {
        const parsedDocumentation = filesToUpload
            .filter(({ status }) => status === 'required')
            .map(({ docId }) => docId)
            .join(' ');
        recordEvent(
            PATH_DOCUMENTATION_RETRY,
            `documentacion ${parsedDocumentation}`,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            userProfile.investmentType,
            undefined,
            {
                categoriaEvento: 'onboarding',
                accionEvento: 'modal documentacion volver a subir/inicio',
                etiquetaEvento: 'vista',
                tipoPagina: `documentacion ${parsedDocumentation}`,
                tipoProceso: 'onboarding',
                pasoProceso: 'documentacion',
                detalleProceso: 'revision',
            }
        );
    };

    const doTagGetInformationDocumentError = ({ body, statusCode }) => {
        errorTag(body, PATH_DOCUMENTATION, ERROR_EN_DOCUMENTACION, statusCode);
    };

    const doTagModalDocumentationRetry = () => {
        recordEventCustomOnly(
            {
                categoriaEvento: 'onboarding',
                accionEvento: 'modal documentacion volver a subir/exito',
                etiquetaEvento: 'vista',
            },
            PATH_DOCUMENTATION_RETRY
        );
    };
    const doTagDocumentRetryThanksClose = etiquetaEvento => {
        recordEventCustomOnly(
            {
                categoriaEvento: 'onboarding',
                accionEvento: 'modal documentacion volver a subir/exito',
                etiquetaEvento,
            },
            PATH_DOCUMENTATION_RETRY
        );
    };
    const doTagDocumentRetryDocClose = etiquetaEvento => {
        recordEventCustomOnly(
            {
                categoriaEvento: 'onboarding',
                accionEvento: 'modal documentacion volver a subir/inicio',
                etiquetaEvento,
            },
            PATH_DOCUMENTATION_RETRY
        );
    };

    return {
        doTagDocumentRetry,
        doTagDocumentRetryDocClose,
        doTagDocumentRetryThanksClose,
        doTagGetInformationDocumentError,
        doTagDocumentationPage,
        doTagModalDocumentationRetry,
        doTagRecordStandByPage,
    };
};

export default buildTagging;
